import {
  dateFormatterDayMonthLongYearAndHourMinute2Digits,
  dateFormatterDayMonthYearLong2Digits,
  dateFormatterLongMonthYear,
  transformStrDateToMMYY,
} from "@qivia/ui";

export const formatCurrency = (value: number) =>
  hasBadNumberValue(value)
    ? ""
    : new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(value);

export const formatPercent = (value: number) =>
  hasBadNumberValue(value / 100)
    ? ""
    : new Intl.NumberFormat("fr-FR", {
        style: "percent",
      }).format(value / 100);

export const formatMileage = (value: number) => {
  return hasBadNumberValue(value)
    ? ""
    : new Intl.NumberFormat("fr-FR", {
        style: "unit",
        unit: "kilometer",
        maximumFractionDigits: 1,
      }).format(value);
};

export const formatLiter = (value: number) => {
  return hasBadNumberValue(value)
    ? ""
    : new Intl.NumberFormat("fr-FR", {
        style: "unit",
        unit: "liter",
      }).format(value);
};

export const formatHour = (value: number) => {
  if (hasBadNumberValue(value)) return "";

  const isNegative = value < 0;
  const hours = Math.floor(Math.abs(value));
  const minutes = Math.round((Math.abs(value) - hours) * 60);

  const hoursFormatted = hours
    ? new Intl.NumberFormat("fr-FR", {
        style: "unit",
        unit: "hour",
      }).format(hours)
    : "";

  return (
    (isNegative ? "-" : "") +
    hoursFormatted +
    new Intl.NumberFormat("fr-FR", {
      style: "unit",
      unit: "minute",
    }).format(minutes)
  );
};

export const formatLiterPerOneHundredMileage = (value: number) => {
  return hasBadNumberValue(value)
    ? ""
    : new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: 2,
      }).format(value) + " l/100 km";
};

export const formatCarbonEmissionPerKilometer = (value: number) => {
  return hasBadNumberValue(value)
    ? ""
    : new Intl.NumberFormat("fr-FR", {
        style: "unit",
        unit: "gram-per-kilometer",
        maximumFractionDigits: 0,
      }).format(value);
};

export const formatMonth = (value: number) =>
  hasBadNumberValue(value)
    ? ""
    : new Intl.NumberFormat("fr-FR", {
        style: "unit",
        unit: "month",
        unitDisplay: "long",
      }).format(value);

export const hasBadNumberValue = (value: number) => {
  return isNaN(value) || value === undefined || value === null;
};

export const formatDate = (value: string | number | Date) =>
  !isNaN(new Date(value).getTime())
    ? dateFormatterDayMonthYearLong2Digits(new Date(value))
    : "";

export const formatDateWithoutDay = (value: string | number | Date) =>
  !isNaN(new Date(value).getTime())
    ? dateFormatterLongMonthYear(new Date(value))
    : "";

export const formatDateTime = (value: string | number | Date) =>
  !isNaN(new Date(value).getTime())
    ? dateFormatterDayMonthLongYearAndHourMinute2Digits(new Date(value))
    : "";

export const formatDateHourMinute = (value: string | number | Date) =>
  !isNaN(new Date(value).getTime())
    ? new Intl.DateTimeFormat("fr-FR", {
        hour: "numeric",
        minute: "numeric",
      })
        .format(new Date(value))
        .replace(":", "h")
    : "";

export const getDate = (value: string) => {
  if (!value) return null;
  const parts = value.split("/");
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return new Date(`${month}/${day}/${year}`);
  }
  if (parts.length === 1) {
    return transformStrDateToMMYY(parts[0]);
  }

  return null;
};

export const getDateTime = (value: string) => {
  if (!value) return value;
  const [day, month, rest] = value.split("/");
  const [year, restHours] = rest.split(" à ");
  const [hour, minute] = restHours.split("h");

  return new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hour),
    parseInt(minute),
  );
};
